/* eslint-disable react/display-name */
import { Tooltip } from 'aa_common/front-end/antd'
import { ReactComponent as CheckCircleIcon } from 'aa_common/front-end/assets/icons/check-circle.svg'
import { If } from 'aa_common/front-end/components'
import { everyTrue, ifElse, isEmptyValue, someTrue } from 'aa_common/front-end/helpers'
import { ColumnsType } from 'antd/lib/table'
import { DATE_FORMAT } from 'common/constants'
import { formatMonth, yen } from 'common/helpers/format-helper'
import { getNs } from 'common/i18n-config'
import { FeatureFlagNames } from 'common/open-features'
import { useCheckFlag } from 'common/open-features/hooks'
import moment from 'moment'
import React from 'react'

import { ButtonLink, CheckIcon } from './styles'

const { t } = getNs(['components/organisms/assets/asset-detail', 'common'])

const MonthYearTemplate = (data: any, fiscalYear: number) => {
  const { isEnable: enablePartialImpairment } = useCheckFlag(FeatureFlagNames.PartialImpairment)
  return (
    <span>
      {ifElse(
        enablePartialImpairment,
        t('format.term_month', { month: formatMonth(data.month) }),
        t('format.term_year_month', { year: fiscalYear, month: formatMonth(data.month) })
      )}
    </span>
  )
}

const MoneyTemplate = (amount: number) => <span>{amount === undefined ? '-' : yen(amount)}</span>

interface MonthSimulationTableSettingsProps {
  isAccountLedger: boolean
  isShowComparison: boolean
  fiscalYear: number
  isSourceAsset: boolean
  showComparisonTable: () => void
  isAccelerated?: boolean
  showImpairmentInfo?: boolean
}

export const monthSimulationUsageTableSettings = ({
  isAccountLedger,
  isShowComparison,
  fiscalYear,
  isSourceAsset,
  showComparisonTable,
  isAccelerated,
  showImpairmentInfo,
}: MonthSimulationTableSettingsProps): ColumnsType<any> => {
  const accumulatedDepreciationKey = ifElse(
    isShowComparison,
    'adjusted_accumulated_depreciation_amount',
    'accumulated_depreciation_amount'
  )

  return [
    {
      width: 30,
      render: () => null,
    },
    {
      width: 120,
      key: 'term_month',
      className: 'depreciation-cell',
      render: data => ({
        children: data?.id ? (
          MonthYearTemplate(data, fiscalYear)
        ) : (
          <>
            <span className="text">{t('depreciationTable.adjust_depreciation')}</span>{' '}
            <ButtonLink type="link" onClick={showComparisonTable}>
              {t('depreciationTable.adjust_depreciation_link')}
            </ButtonLink>
          </>
        ),
        props: { colSpan: ifElse(data?.id, 1, 2) },
      }),
    },
    {
      key: 'depreciation_status',
      width: 302,
      className: 'depreciation-cell',
      render: data => {
        const isRetiredOrSold = data.is_retired_or_sold

        if (!data?.id) {
          return {
            props: { colSpan: 0 },
          }
        }

        if (everyTrue(isRetiredOrSold, data.retiredAt)) {
          return (
            <span className="text">
              {t('depreciationTable.asset_retired_at', {
                retiredAt: moment(data.retiredAt).format(DATE_FORMAT),
              })}
            </span>
          )
        }

        if (everyTrue(isRetiredOrSold, data.soldAt)) {
          return (
            <span className="text">
              {t('depreciationTable.asset_sold_at', {
                soldAt: moment(data.soldAt).format(DATE_FORMAT),
              })}
            </span>
          )
        }

        // full impair
        if (everyTrue(isAccountLedger, data?.is_impaired, data?.impairAt)) {
          return (
            <span className="text">
              {t('depreciationTable.asset_impair_at', {
                impairAt: moment(data.impairAt).format(DATE_FORMAT),
              })}
            </span>
          )
        }

        // partial impair
        if (everyTrue(isAccountLedger, data?.impairLost)) {
          return (
            <span className="text">
              {t('depreciationTable.asset_impair_at', {
                impairAt: moment(data.end_date).format(DATE_FORMAT),
              })}
            </span>
          )
        }

        if (everyTrue(isSourceAsset, data?.isDivided, data?.dividedAt)) {
          return (
            <span className="text">
              {t('depreciationTable.asset_divided_at', {
                dividedAt: moment(data?.dividedAt).format(DATE_FORMAT),
              })}
            </span>
          )
        }

        if (data.monthHasBeenAppliedDepreciation) {
          return <CheckIcon component={CheckCircleIcon} />
        }

        if (data.isActive) {
          return <span className="text">{t('depreciation_calculation_processing')}</span>
        }
      },
    },
    {
      title: t('depreciationTable.beginning_book_value'),
      width: 130,
      dataIndex: 'beginning_book_value',
      key: 'beginning_book_value',
      showSorterTooltip: false,
      className: 'depreciation-cell detail-usage right',
      render: MoneyTemplate,
    },
    {
      title: t('depreciationTable.depreciation_amount'),
      width: 130,
      dataIndex: 'depreciation_amount',
      key: 'depreciation_amount',
      showSorterTooltip: false,
      className: 'depreciation-cell detail-usage right',
      render: (value: number, record: any) => {
        const adjusted = everyTrue(
          !!isAccelerated,
          !isEmptyValue(record?.depreciation_amount),
          !isEmptyValue(record?.original_depreciation_amount),
          record?.depreciation_amount !== record?.original_depreciation_amount
        )

        return (
          <>
            <If condition={someTrue(!adjusted, !isAccountLedger)}>
              <span>{ifElse(value === undefined, '-', yen(value))}</span>
            </If>
            <If condition={everyTrue(adjusted, isAccountLedger)}>
              <Tooltip content={t('adjusted_depreciation_amount')} placement="bottom">
                <span className="adjusted">{ifElse(value === undefined, '-', yen(value))}</span>
              </Tooltip>
            </If>
          </>
        )
      },
    },
    ...ifElse(
      showImpairmentInfo,
      [
        {
          title: t('depreciationTable.impairment_loss'),
          key: 'impairLost',
          dataIndex: 'impairLost',
          width: 120,
          className: `impairment-cell depreciation-cell right`,
          render: MoneyTemplate,
        },
      ],
      []
    ),
    {
      title: t('depreciationTable.end_of_term_book_value'),
      dataIndex: 'end_of_term_book_value',
      key: 'end_of_term_book_value',
      width: 130,
      className: 'depreciation-cell detail-usage right',
      render: MoneyTemplate,
    },
    {
      title: t('depreciationTable.accumulated_depreciation'),
      width: 130,
      className: 'depreciation-cell detail-usage right',
      dataIndex: accumulatedDepreciationKey,
      key: accumulatedDepreciationKey,
      render: MoneyTemplate,
    },
    ...ifElse(
      showImpairmentInfo,
      [
        {
          title: t('depreciationTable.accumulated_impairment_amount'),
          key: 'accumulatedImpairmentAmount',
          dataIndex: 'accumulatedImpairmentAmount',
          width: 120,
          className: `impairment-cell depreciation-cell right`,
          render: MoneyTemplate,
        },
      ],
      []
    ),
  ]
}
